import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchArticles, fetchCategories } from "../services/api";
import ArticleCard from "./ArticleCard";
import Sidebar from "./Sidebar";
import { ArrowLeft, Loader2 } from "lucide-react";

const CategoryPage = () => {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [catData, cats] = await Promise.all([
          fetchArticles({ category: categoryId }),
          fetchCategories(),
        ]);
        setArticles(catData.articles || []);
        setCategories(cats);
        setCategory(cats.find((c) => c.id === categoryId) || null);
      } catch (err) {
        console.error("Failed to load articles:", err);
      }
      setLoading(false);
    };
    loadData();
  }, [categoryId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-[#1B7A3D]" size={32} />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Category not found</h1>
        <Link to="/" className="text-[#1B7A3D] font-medium hover:underline">
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Category Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 text-sm mb-4">
            <Link
              to="/"
              className="text-gray-500 hover:text-[#1B7A3D] transition-colors flex items-center gap-1"
            >
              <ArrowLeft size={14} />
              Home
            </Link>
            <span className="text-gray-300">/</span>
            <span className="font-medium" style={{ color: category.color }}>
              {category.name}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="w-1.5 h-10 rounded-full"
              style={{ backgroundColor: category.color }}
            />
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-gray-900">
                {category.name}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {articles.length} article{articles.length !== 1 ? "s" : ""} · Opinion & Analysis
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1B7A3D]" size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Articles */}
            <div className="lg:col-span-2">
              {articles.length > 0 ? (
                <>
                  {/* First article large */}
                  {articles[0] && (
                    <div className="mb-8">
                      <ArticleCard article={articles[0]} />
                    </div>
                  )}
                  {/* Rest horizontal */}
                  <div className="space-y-2">
                    {articles.slice(1).map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        variant="horizontal"
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-16">
                  <p className="text-gray-500 text-lg">No articles in this category yet.</p>
                  <Link to="/write" className="text-[#1B7A3D] font-medium mt-2 inline-block hover:underline">
                    Write the first article
                  </Link>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <Sidebar categories={categories} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
